import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Invoices from './pages/Invoices';
import UploadInvoice from './pages/UploadInvoice';
import Users from './pages/Users';
import Totals from './pages/Totals';
import TransactionHistory from './pages/TransactionHistory';
import ActivityLog from './pages/ActivityLog';
import EmailExtractions from './pages/EmailExtractions';
import DailyDeposits from './pages/DailyDeposits';
import StripeHistory from './pages/StripeHistory';
import ExtraAccounting from './pages/ExtraAccounting';
import Trash from './pages/Trash';
import PayPalTransactions from './pages/PayPalTransactions';
import { PageTitle } from './components/PageTitle';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles, allowFlag }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="spinner"></div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    if (allowedRoles && !allowedRoles.includes(user.role) && !(allowFlag && user[allowFlag])) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

// Public Route - redirect to appropriate page based on role if logged in
const PublicRoute = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="spinner"></div>
            </div>
        );
    }

    if (user) {
        // Regular users go to upload page first, others go to dashboard
        const redirectTo = user.role === 'usuario' ? '/dashboard/upload' : '/dashboard';
        return <Navigate to={redirectTo} replace />;
    }

    return children;
};

// Home redirect based on user role
const HomeRedirect = () => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="spinner"></div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // Regular users go to upload page first, others go to dashboard
    const redirectTo = user.role === 'usuario' ? '/dashboard/upload' : '/dashboard';
    return <Navigate to={redirectTo} replace />;
};

function AppRoutes() {
    return (
        <>
        <PageTitle />
        <Routes>
            {/* Public Routes */}
            <Route
                path="/login"
                element={
                    <PublicRoute>
                        <Login />
                    </PublicRoute>
                }
            />

            {/* Protected Routes */}
            <Route
                path="/dashboard"
                element={
                    <ProtectedRoute>
                        <Dashboard />
                    </ProtectedRoute>
                }
            >
                <Route
                    path="invoices"
                    element={<Invoices />}
                />
                <Route
                    path="upload"
                    element={
                        <ProtectedRoute allowedRoles={['admin', 'usuario']}>
                            <UploadInvoice />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="users"
                    element={
                        <ProtectedRoute allowedRoles={['admin', 'supervisor', 'supervisor2']}>
                            <Users />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="totals"
                    element={
                        <ProtectedRoute allowedRoles={['admin', 'supervisor']}>
                            <Totals />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="history"
                    element={
                        <ProtectedRoute allowedRoles={['admin', 'supervisor']}>
                            <TransactionHistory />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="activities"
                    element={
                        <ProtectedRoute allowedRoles={['admin', 'supervisor']}>
                            <ActivityLog />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="email-extractions"
                    element={
                        <ProtectedRoute allowedRoles={['admin', 'supervisor']}>
                            <EmailExtractions />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="daily-deposits"
                    element={
                        <ProtectedRoute allowedRoles={['admin']}>
                            <DailyDeposits />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="stripe-history"
                    element={
                        <ProtectedRoute allowedRoles={['admin']}>
                            <StripeHistory />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="extra-accounting"
                    element={
                        <ProtectedRoute allowedRoles={['admin']}>
                            <ExtraAccounting />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="trash"
                    element={
                        <ProtectedRoute allowedRoles={['admin']}>
                            <Trash />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="paypal"
                    element={
                        <ProtectedRoute allowedRoles={['admin']} allowFlag="is_paypal">
                            <PayPalTransactions />
                        </ProtectedRoute>
                    }
                />
            </Route>

            {/* Redirect root based on user role */}
            <Route path="/" element={<HomeRedirect />} />
            
            {/* Catch all - redirect based on role */}
            <Route path="*" element={<HomeRedirect />} />
        </Routes>
        </>
    );
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <AppRoutes />
                <Toaster position="top-right" richColors />
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
